<template>
  <v-card>
    <v-toolbar color="purple darken-2" dark dense flat>
      <v-icon class="mr-2">mdi-swap-horizontal</v-icon>
      <v-toolbar-title class="subtitle-1 font-weight-bold">
        <div>Cambio de Programa — Solicitudes</div>
        <div v-if="nombreEstudiante" class="caption purple--text text--lighten-3" style="line-height:1.2;font-weight:400">
          {{ nombreEstudiante }}
        </div>
      </v-toolbar-title>
      <v-spacer />
      <v-btn icon @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
    </v-toolbar>

    <v-card-text class="pa-4">

      <!-- Acción nueva solicitud -->
      <div class="d-flex align-center pa-3 mb-3 rounded" style="background:#f3e5f5;border-left:4px solid #7b1fa2">
        <v-icon color="purple darken-2" class="mr-3">mdi-swap-horizontal-bold</v-icon>
        <div>
          <div class="overline" style="font-size:10px;color:#546e7a">Inscripción</div>
          <div class="body-2 font-weight-bold purple--text text--darken-2">#{{ inscripcionId }}</div>
        </div>
        <v-spacer />
        <v-btn small color="purple darken-2" dark
          :disabled="haySolicitudPendiente"
          @click="abrirNuevaSolicitud"
        >
          <v-icon left small>mdi-plus</v-icon>Nueva solicitud
        </v-btn>
      </div>

      <v-alert v-if="haySolicitudPendiente" type="info" dense outlined class="mb-3" icon="mdi-information-outline">
        Ya existe una solicitud <strong>pendiente</strong>. Debe resolverse antes de crear otra.
      </v-alert>

      <!-- Cargando -->
      <div v-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="purple darken-2" />
        <div class="caption grey--text mt-2">Cargando historial...</div>
      </div>

      <!-- Sin historial -->
      <div v-else-if="historial.length === 0" class="text-center py-8 grey--text">
        <v-icon large color="grey lighten-2">mdi-swap-horizontal</v-icon>
        <div class="mt-2 caption">Sin solicitudes de cambio para esta inscripción.</div>
      </div>

      <!-- Timeline -->
      <v-timeline v-else dense clipped>
        <v-timeline-item
          v-for="sol in historial"
          :key="sol.id"
          :color="colorEstado(sol.estado)"
          small
        >
          <v-card outlined class="mb-2 rounded">
            <v-card-text class="py-2 px-3">
              <!-- Fila superior -->
              <div class="d-flex align-center flex-wrap mb-2" style="gap:8px">
                <v-chip x-small :color="colorEstado(sol.estado)" dark>{{ sol.estado }}</v-chip>
                <span class="caption grey--text">{{ formatFecha(sol.fecha_registro) }}</span>
                <v-spacer />
                <template v-if="sol.estado === 'SOLICITADO' && puedeAprobar">
                  <v-btn x-small color="green darken-1" dark
                    :loading="procesando === sol.id + '_ap'"
                    :disabled="procesando !== null"
                    @click="abrirAprobar(sol)"
                  >
                    <v-icon left x-small>mdi-check</v-icon>Aprobar
                  </v-btn>
                  <v-btn x-small color="red darken-1" dark
                    :loading="procesando === sol.id + '_re'"
                    :disabled="procesando !== null"
                    @click="abrirRechazar(sol)"
                  >
                    <v-icon left x-small>mdi-close</v-icon>Rechazar
                  </v-btn>
                </template>
              </div>

              <!-- Cambio de programa -->
              <div class="d-flex align-start mb-1" style="gap:8px">
                <div>
                  <v-chip x-small outlined color="grey darken-1">#{{ sol.programa_origen_id }}</v-chip>
                  <div v-if="programaDetalles[sol.programa_origen_id]" class="caption grey--text mt-1" style="max-width:180px;line-height:1.2">
                    {{ programaDetalles[sol.programa_origen_id].codigo }} — {{ programaDetalles[sol.programa_origen_id].nombre_compuesto }}
                  </div>
                </div>
                <v-icon x-small color="purple darken-2" class="mt-1">mdi-arrow-right</v-icon>
                <div>
                  <v-chip x-small color="purple darken-2" dark>#{{ sol.programa_destino_id }}</v-chip>
                  <div v-if="programaDetalles[sol.programa_destino_id]" class="caption purple--text text--darken-2 mt-1" style="max-width:180px;line-height:1.2">
                    {{ programaDetalles[sol.programa_destino_id].codigo }} — {{ programaDetalles[sol.programa_destino_id].nombre_compuesto }}
                  </div>
                </div>
              </div>

              <!-- Descripción -->
              <div class="caption grey--text text--darken-1 mt-1" v-if="sol.descripcion">
                <v-icon x-small class="mr-1">mdi-text-box-outline</v-icon>{{ sol.descripcion }}
              </div>

              <!-- Respuesta/motivo rechazo -->
              <div class="caption mt-1" v-if="sol.respuesta">
                <v-icon x-small color="red darken-1" class="mr-1">mdi-message-reply-text</v-icon>
                <span class="red--text text--darken-1">Motivo: {{ sol.respuesta }}</span>
              </div>

              <!-- Inscripción destino (si aprobada) -->
              <div class="caption green--text text--darken-2 mt-1" v-if="sol.inscripcion_destino_id">
                <v-icon x-small color="green darken-2" class="mr-1">mdi-check-circle</v-icon>
                Inscripción destino: #{{ sol.inscripcion_destino_id }}
              </div>
            </v-card-text>
          </v-card>
        </v-timeline-item>
      </v-timeline>
    </v-card-text>

    <!-- ── Dialog nueva solicitud ── -->
    <v-dialog v-model="dialogSolicitud" max-width="520" persistent>
      <v-card>
        <v-card-title class="py-4 px-5">
          <v-icon color="purple darken-2" class="mr-2">mdi-swap-horizontal-bold</v-icon>
          <span class="subtitle-1 font-weight-bold">Nueva Solicitud de Cambio</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4 px-5 pb-1">

          <!-- 1. Sede -->
          <v-select
            v-model="form.sede_id"
            :items="sedes"
            item-text="nombre"
            item-value="id"
            label="Sede destino *"
            outlined dense
            prepend-inner-icon="mdi-map-marker"
            :loading="loadingSedes"
            @change="onChangeSede"
            class="mb-3"
          />

          <!-- 2. Programa de esa sede -->
          <v-autocomplete
            v-model="form.programa_destino_id"
            :items="programasOptions"
            :loading="loadingProgramas"
            item-text="text"
            item-value="value"
            label="Programa destino"
            outlined dense
            prepend-inner-icon="mdi-school-outline"
            :disabled="!form.sede_id"
            no-data-text="Sin programas para esta sede"
            clearable
            class="mb-1"
          />

          <!-- 3. Motivo -->
          <v-select
            v-model="form.motivo_cambio_programa_id"
            :items="motivos"
            item-text="nombre"
            item-value="id"
            label="Motivo del cambio *"
            outlined dense
            prepend-inner-icon="mdi-tag-outline"
            :loading="loadingMotivos"
            no-data-text="Sin motivos disponibles"
            class="mb-3"
          />

          <!-- 4. Justificación -->
          <v-textarea
            v-model="form.descripcion"
            label="Comentario adicional (opcional)"
            outlined dense rows="3"
            placeholder="Descripción adicional del cambio de programa..."
            counter="500"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-5 py-3">
          <v-spacer />
          <v-btn text small @click="cerrarDialogSolicitud" :disabled="creando">Cancelar</v-btn>
          <v-btn color="purple darken-2" dark small
            :loading="creando"
            :disabled="!programaDestinoFinal || !form.motivo_cambio_programa_id"
            @click="crearSolicitud"
          >
            <v-icon left small>mdi-send</v-icon>Enviar solicitud
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Dialog rechazar ── -->
    <v-dialog v-model="dialogRechazar" max-width="420" persistent>
      <v-card>
        <v-card-title class="py-4 px-5">
          <v-icon color="red darken-1" class="mr-2">mdi-close-circle-outline</v-icon>
          <span class="subtitle-1 font-weight-bold">Rechazar solicitud</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4 px-5 pb-1">
          <v-textarea
            v-model="formRechazar.respuesta"
            label="Motivo del rechazo *"
            outlined dense rows="3"
            placeholder="Indica el motivo por el que se rechaza..."
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-5 py-3">
          <v-spacer />
          <v-btn text small @click="dialogRechazar = false" :disabled="procesando !== null">Cancelar</v-btn>
          <v-btn color="red darken-1" dark small
            :loading="procesando !== null"
            :disabled="!formRechazar.respuesta"
            @click="rechazar"
          >
            <v-icon left small>mdi-close</v-icon>Rechazar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Dialog aprobar ── -->
    <v-dialog v-model="dialogAprobar" max-width="900" persistent scrollable>
      <v-card>
        <v-toolbar color="green darken-1" dark dense flat>
          <v-icon class="mr-2">mdi-check-circle-outline</v-icon>
          <v-toolbar-title class="subtitle-1 font-weight-bold">Aprobar cambio de programa</v-toolbar-title>
        </v-toolbar>

        <v-progress-linear v-if="loadingDetalles" indeterminate color="green darken-2" />

        <v-card-text class="pa-4">

          <!-- Programas origen → destino -->
          <v-row dense class="mb-4">
            <v-col cols="12" sm="5">
              <v-card outlined>
                <v-card-title class="caption font-weight-bold py-2 px-3 grey lighten-4">
                  <v-icon small class="mr-1" color="grey darken-1">mdi-school</v-icon>Programa Origen
                </v-card-title>
                <v-card-text class="py-2 px-3">
                  <div v-if="programaOrigen">
                    <div class="caption font-weight-bold">#{{ programaOrigen.id }} — {{ programaOrigen.codigo }}</div>
                    <div class="caption" style="line-height:1.3">{{ programaOrigen.nombre_compuesto }}</div>
                    <!-- Plan cobro origen -->
                    <div v-if="programaOrigen.plan_programa || (programaOrigen.planes && programaOrigen.planes.length)" class="mt-2">
                      <div class="overline" style="font-size:9px;color:#1565c0">
                        Plan cobro: {{ (programaOrigen.plan_programa || programaOrigen.planes[0]).nombre }}
                      </div>
                      <v-simple-table dense style="background:transparent">
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="caption px-1">Concepto</th>
                              <th class="caption px-1">Monto</th>
                              <th class="caption px-1">Cuotas</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(c, i) in ((programaOrigen.plan_programa || programaOrigen.planes[0]).cobros || (programaOrigen.plan_programa || programaOrigen.planes[0]).cobros_programa || [])"
                              :key="i"
                            >
                              <td class="caption px-1">{{ c.concepto_pago && c.concepto_pago.nombre ? c.concepto_pago.nombre : `Concepto ${i+1}` }}</td>
                              <td class="caption px-1 blue--text font-weight-bold">{{ c.monto }}</td>
                              <td class="caption px-1">{{ c.cuotas }}</td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </div>
                  </div>
                  <div v-else class="caption grey--text">Cargando...</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" sm="2" class="d-flex align-center justify-center">
              <v-icon large color="purple darken-2">mdi-arrow-right</v-icon>
            </v-col>
            <v-col cols="12" sm="5">
              <v-card outlined style="border-color:#7b1fa2">
                <v-card-title class="caption font-weight-bold py-2 px-3" style="background:#f3e5f5">
                  <v-icon small class="mr-1" color="purple darken-2">mdi-school</v-icon>
                  <span class="purple--text text--darken-2">Programa Destino</span>
                </v-card-title>
                <v-card-text class="py-2 px-3">
                  <div v-if="programaDestino">
                    <div class="caption font-weight-bold purple--text text--darken-2">#{{ programaDestino.id }} — {{ programaDestino.codigo }}</div>
                    <div class="caption" style="line-height:1.3">{{ programaDestino.nombre_compuesto }}</div>
                  </div>
                  <div v-else class="caption grey--text">Cargando...</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Plan de pagos actual (detallado) -->
          <div class="subtitle-2 font-weight-bold mb-2 blue--text text--darken-3">
            <v-icon small color="blue darken-3" class="mr-1">mdi-receipt</v-icon>Plan de Pagos Actual del Estudiante
          </div>
          <div v-if="loadingDetalles" class="caption grey--text mb-3">Cargando plan de pagos...</div>
          <div v-else-if="planPagoActual.length === 0" class="caption grey--text mb-3">Sin cuotas registradas.</div>
          <div v-else class="mb-4" style="overflow-x:auto">
            <table style="width:100%;border-collapse:collapse;font-size:12px">
              <thead>
                <tr style="background:#1565c0;color:white">
                  <th class="px-2 py-1 text-left">Concepto</th>
                  <th class="px-2 py-1 text-center">N°Cuota</th>
                  <th class="px-2 py-1 text-right">Total</th>
                  <th class="px-2 py-1 text-right">M. Pagado</th>
                  <th class="px-2 py-1 text-right">M. Descuento</th>
                  <th class="px-2 py-1 text-right">M. Regularizado</th>
                  <th class="px-2 py-1 text-right">M. Compensacion</th>
                  <th class="px-2 py-1 text-right">M. Liquido</th>
                  <th class="px-2 py-1 text-right">Saldo</th>
                  <th class="px-2 py-1 text-left">Fecha Venc.</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(cuota, i) in planPagoActual" :key="cuota.id || i"
                  :style="i % 2 === 0 ? 'background:#f5f5f5' : 'background:white'"
                >
                  <td class="px-2 py-1">{{ cuota.concepto_pago && cuota.concepto_pago.nombre ? cuota.concepto_pago.nombre : cuota.concepto_pago }}</td>
                  <td class="px-2 py-1 text-center">{{ cuota.nro_cuota }}</td>
                  <td class="px-2 py-1 text-right font-weight-medium">{{ cuota.total || cuota.monto }}</td>
                  <td class="px-2 py-1 text-right" style="color:#2e7d32">{{ cuota.monto_pagado }}</td>
                  <td class="px-2 py-1 text-right">{{ cuota.monto_descuento }}</td>
                  <td class="px-2 py-1 text-right">{{ cuota.monto_regularizado }}</td>
                  <td class="px-2 py-1 text-right">{{ cuota.monto_compensacion }}</td>
                  <td class="px-2 py-1 text-right">{{ cuota.monto_liquido }}</td>
                  <td class="px-2 py-1 text-right font-weight-medium" :style="Number(cuota.saldo) > 0 ? 'color:#e65100' : 'color:#2e7d32'">{{ cuota.saldo }}</td>
                  <td class="px-2 py-1">{{ cuota.fecha_vencimiento || cuota.fecha_pago }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <v-divider class="mb-4" />

          <!-- Plan del programa destino -->
          <v-select
            v-model="selectedPlan"
            :items="planesDestino"
            :item-text="p => p.nombre || `Plan #${p.plan_cobro_programa_id || p.id}`"
            :item-value="p => p"
            label="Plan de cobro del programa destino *"
            outlined dense return-object
            prepend-inner-icon="mdi-cash-multiple"
            @change="onSelectPlan"
            class="mb-2"
          />

          <!-- Plan de pagos nuevo (editable) -->
          <template v-if="planPagos.length > 0">
            <div class="subtitle-2 font-weight-bold mb-2 mt-2 purple--text text--darken-2">
              <v-icon small color="purple darken-2" class="mr-1">mdi-cash-multiple</v-icon>Plan de Pagos Nuevo (editable)
            </div>
            <v-simple-table dense style="border:1px solid #ce93d8;border-radius:4px">
              <template v-slot:default>
                <thead style="background:#f3e5f5">
                  <tr>
                    <th class="caption">Concepto</th>
                    <th class="caption text-center">Cuota</th>
                    <th class="caption" style="width:140px">Monto (Bs)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(p, i) in planPagos" :key="i">
                    <td class="caption">{{ p.concepto_nombre }}</td>
                    <td class="caption text-center">{{ p.nro_cuota }}</td>
                    <td>
                      <v-text-field
                        v-model.number="p.monto"
                        type="number" min="0"
                        dense hide-details outlined
                        style="max-width:130px"
                      />
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </template>
        </v-card-text>

        <v-divider />
        <v-card-actions class="px-5 py-3">
          <v-spacer />
          <v-btn text small @click="dialogAprobar = false" :disabled="procesando !== null">Cancelar</v-btn>
          <v-btn color="green darken-1" dark small
            :loading="procesando !== null"
            :disabled="!selectedPlan || planPagos.length === 0"
            @click="confirmarAprobar"
          >
            <v-icon left small>mdi-check</v-icon>Confirmar aprobación
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right :timeout="4000">
      {{ snackbar.msg }}
      <template v-slot:action>
        <v-btn text small @click="snackbar.show = false">OK</v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import xaxios from '@/xaxios';
import gql from 'graphql-tag';

const API = process.env.VUE_APP_API_REST;

export default {
  name: 'SolicitudCambioPrograma',

  props: {
    inscripcionId:   { type: [Number, String], required: true },
    nombreEstudiante:{ type: String, default: null },
    programaOrigenId:{ type: [Number, String], default: null },
  },

  data() {
    return {
      historial: [],
      loading: false,
      creando: false,
      procesando: null,

      dialogSolicitud: false,
      dialogRechazar:  false,
      dialogAprobar:   false,

      form: { sede_id: null, programa_destino_id: null, motivo_cambio_programa_id: null, descripcion: '' },
      motivos: [],
      loadingMotivos: false,
      formRechazar: { respuesta: '' },
      solicitudAgestionar: null,

      sedes: [],
      loadingSedes: false,
      programasOptions: [],
      loadingProgramas: false,

      programaDetalles: {},
      programaOrigen: null,
      programaDestino: null,
      loadingDetalles: false,

      planesDestino: [],
      selectedPlan: null,
      planPagos: [],
      planPagoActual: [],

      snackbar: { show: false, msg: '', color: 'success' },
    };
  },

  computed: {
    haySolicitudPendiente() {
      return this.historial.some(s => s.estado === 'SOLICITADO');
    },
    puedeAprobar() {
      const id = this.$store.state.CARGO.id;
      return id === 1 || id === 3 || id === 19;
    },
    programaDestinoFinal() {
      return this.form.programa_destino_id || null;
    },
  },

  methods: {
    async cargarHistorial() {
      this.loading = true;
      try {
        const { data } = await xaxios.get(
          `${API}/api/v1/inscripcion/${this.inscripcionId}/cambio-programa/solicitudes`
        );
        this.historial = Array.isArray(data.data?.historial) ? data.data.historial : [];
        this.cargarDetallesProgramas();
      } catch {
        this.historial = [];
      } finally {
        this.loading = false;
      }
    },

    cargarDetallesProgramas() {
      const ids = [...new Set(
        this.historial.flatMap(s => [s.programa_origen_id, s.programa_destino_id]).filter(Boolean)
      )];
      ids.forEach(async id => {
        if (this.programaDetalles[id]) return;
        try {
          const { data } = await xaxios.get(`${API}/esam/programa/id/${id}`);
          this.$set(this.programaDetalles, id, {
            codigo: data.codigo,
            nombre_compuesto: data.nombre_compuesto,
          });
        } catch (e) { window.console.warn('Error cargando programa', e); }
      });
    },

    async abrirNuevaSolicitud() {
      this.form.sede_id = null;
      this.form.programa_destino_id = null;
      this.form.motivo_cambio_programa_id = null;
      this.form.descripcion = '';
      this.programasOptions = [];
      this.dialogSolicitud = true;
      await Promise.all([this.cargarSedes(), this.cargarMotivos()]);
    },

    async cargarMotivos() {
      if (this.motivos.length) return;
      this.loadingMotivos = true;
      try {
        const { data } = await xaxios.get(`${API}/api/v1/cambio-programa/motivos`);
        this.motivos = (data.data?.data || []).filter(m => m.activo);
      } catch {
        this.motivos = [];
      } finally {
        this.loadingMotivos = false;
      }
    },

    async cargarSedes() {
      this.loadingSedes = true;
      try {
        const unidad = this.$store.state.CARGO.sede.unidad_negocio;
        const sql = gql`query{ getSedes{ id, nombre, unidad_negocio } }`;
        const { data } = await this.$apollo.query({ query: sql, fetchPolicy: 'network-only' });
        this.sedes = (data.getSedes || []).filter(s => s.unidad_negocio === unidad);
      } catch {
        this.sedes = [];
      } finally {
        this.loadingSedes = false;
      }
    },

    async onChangeSede(sedeId) {
      this.form.programa_destino_id = null;
      this.form.programa_id_manual = '';
      this.programasOptions = [];
      if (!sedeId) return;
      await this.cargarProgramas(sedeId);
    },

    async cargarProgramas(sedeId) {
      this.loadingProgramas = true;
      try {
        const sql = gql`query{ getProgramas(cursor:1000000,estado:true,idsede:${sedeId}){ id,codigo,nombre_compuesto } }`;
        const { data } = await this.$apollo.query({ query: sql, fetchPolicy: 'network-only' });
        this.programasOptions = (data.getProgramas || [])
          .filter(p => p.id !== parseInt(this.programaOrigenId))
          .map(p => ({ text: `#${p.id} | ${p.codigo} | ${p.nombre_compuesto}`, value: p.id }));
      } catch {
        this.programasOptions = [];
      } finally {
        this.loadingProgramas = false;
      }
    },

    async crearSolicitud() {
      this.creando = true;
      try {
        await xaxios.post(
          `${API}/api/v1/inscripcion/${this.inscripcionId}/cambio-programa/solicitudes`,
          {
            programa_destino_id:        this.programaDestinoFinal,
            motivo_cambio_programa_id:  this.form.motivo_cambio_programa_id,
            descripcion:                this.form.descripcion || undefined,
          }
        );
        this.notificar('Solicitud enviada correctamente', 'success');
        this.cerrarDialogSolicitud();
        await this.cargarHistorial();
        this.$emit('solicitud-creada');
      } catch (e) {
        const msg = e?.response?.data?.message || e?.response?.data?.data || 'Error al enviar la solicitud';
        this.notificar(msg, 'error');
      } finally {
        this.creando = false;
      }
    },

    cerrarDialogSolicitud() {
      this.dialogSolicitud = false;
      this.form.sede_id = null;
      this.form.programa_destino_id = null;
      this.form.motivo_cambio_programa_id = null;
      this.form.descripcion = '';
    },

    abrirRechazar(sol) {
      this.solicitudAgestionar = sol;
      this.formRechazar.respuesta = '';
      this.dialogRechazar = true;
    },

    async rechazar() {
      this.procesando = this.solicitudAgestionar.id + '_re';
      try {
        await xaxios.patch(
          `${API}/api/v1/cambio-programa/solicitudes/${this.solicitudAgestionar.id}`,
          { estado: 'RECHAZADO', respuesta: this.formRechazar.respuesta }
        );
        this.notificar('Solicitud rechazada', 'warning');
        this.dialogRechazar = false;
        await this.cargarHistorial();
      } catch (e) {
        const msg = e?.response?.status === 403
          ? 'No tienes permiso para rechazar en esta sede'
          : e?.response?.data?.message || e?.response?.data?.data || 'Error al rechazar';
        this.notificar(msg, 'error');
      } finally {
        this.procesando = null;
      }
    },

    async abrirAprobar(sol) {
      this.solicitudAgestionar = sol;
      this.selectedPlan = null;
      this.planPagos = [];
      this.planPagoActual = [];
      this.planesDestino = [];
      this.programaOrigen = null;
      this.programaDestino = null;
      this.loadingDetalles = true;
      this.dialogAprobar = true;

      try {
        const results = await Promise.allSettled([
          xaxios.get(`${API}/esam/programa/id/${sol.programa_origen_id}`),
          xaxios.get(`${API}/esam/programa/id/${sol.programa_destino_id}`),
          xaxios.get(`${API}/api/v1/inscripcion/${this.inscripcionId}/plan-pago`),
        ]);

        if (results[0].status === 'fulfilled') {
          const d = results[0].value.data;
          this.programaOrigen = d;
          this.$set(this.programaDetalles, sol.programa_origen_id, { codigo: d.codigo, nombre_compuesto: d.nombre_compuesto });
        }
        if (results[1].status === 'fulfilled') {
          const d = results[1].value.data;
          this.programaDestino = d;
          this.planesDestino = d.planes || [];
          this.$set(this.programaDetalles, sol.programa_destino_id, { codigo: d.codigo, nombre_compuesto: d.nombre_compuesto });
        }
        if (results[2].status === 'fulfilled') {
          this.planPagoActual = results[2].value.data?.data || [];
        }
      } catch (e) {
        window.console.warn('Error cargando datos de aprobación', e);
      } finally {
        this.loadingDetalles = false;
      }
    },

    onSelectPlan(plan) {
      if (!plan) { this.planPagos = []; return; }
      const cobros = plan.cobros || plan.cobros_programa || [];
      this.planPagos = cobros.flatMap((c) => {
        const cuotas = c.cuotas || 1;
        const montoPorCuota = +(parseFloat(c.monto || 0) / cuotas).toFixed(2);
        const nombre = c.concepto_pago?.nombre || `Concepto ${c.concepto_pago_id || c.id}`;
        return Array.from({ length: cuotas }, (_, i) => ({
          cobro_programa_id: c.cobro_programa_id || c.id,
          concepto_pago_id:  c.concepto_pago_id || c.concepto_pago?.id,
          concepto_nombre:   nombre,
          monto:             montoPorCuota,
          nro_cuota:         i + 1,
        }));
      });
    },

    async confirmarAprobar() {
      if (!this.selectedPlan) { this.notificar('Seleccione un plan de cobro', 'warning'); return; }

      this.procesando = this.solicitudAgestionar.id + '_ap';
      try {
        await xaxios.patch(
          `${API}/api/v1/cambio-programa/solicitudes/${this.solicitudAgestionar.id}`,
          {
            estado: 'APROBADO',
            plan_cobro_programa_id: this.selectedPlan.plan_cobro_programa_id || this.selectedPlan.id,
            plan_pagos: this.planPagos.map(p => ({
              cobro_programa_id: p.cobro_programa_id,
              concepto_pago_id:  p.concepto_pago_id,
              monto:             parseFloat(p.monto),
              nro_cuota:         p.nro_cuota,
            })),
          }
        );
        this.notificar('Solicitud aprobada. Transferencia procesada.', 'success');
        this.dialogAprobar = false;
        await this.cargarHistorial();
        this.$emit('aprobada');
      } catch (e) {
        const msg = e?.response?.status === 403
          ? 'No tienes permiso para aprobar en la sede destino'
          : e?.response?.data?.message || e?.response?.data?.data || 'Error al aprobar';
        this.notificar(msg, 'error');
      } finally {
        this.procesando = null;
      }
    },

    colorEstado(estado) {
      const map = { SOLICITADO: 'orange darken-2', APROBADO: 'green darken-2', RECHAZADO: 'red darken-1' };
      return map[estado] || 'grey';
    },

    formatFecha(iso) {
      if (!iso) return '—';
      try {
        return new Date(iso).toLocaleDateString('es-BO', {
          day: '2-digit', month: 'short', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        });
      } catch { return iso; }
    },

    notificar(msg, color = 'success') {
      this.snackbar = { show: true, msg, color };
    },
  },

  mounted() {
    this.cargarHistorial();
  },
};
</script>
